import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Form, InputGroup, Spinner } from "react-bootstrap";
import Select, { ValueType } from "react-select";
import {
  RenewedTeacherInfo,
  School,
  TeacherInfo,
} from "../../../../../store/onboarding/types";
import RoleSelector from "./RoleSelector";
import { ApplicationState } from "../../../../../store";
import { connect, useDispatch, useSelector } from "react-redux";
import { districtRoles } from "../../../../../store/auth/constants";
import {
  changeUserStatus,
  getSchools,
} from "../../../../../store/onboarding/actions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faUndo } from "@fortawesome/free-solid-svg-icons";
import { showConfirmDialog } from "../../../../../store/confirm-dialog/actions";
import { resetPassword } from "../../../../../store/auth/actions";
import { useLoading } from "../../../../../utils/hooks/useLoading";
import { toastr } from "react-redux-toastr";
import { escapeSpecialCharacter, splitStudentName } from "../../../../../utils/NamesUtils";
import useUserRole from "../../../../../utils/hooks/useUserRole";

type StateProps = {
  hasDistrictPermission?: boolean;
  schools: Array<School>;
  selectedTeacher?: TeacherInfo;
};
type DispatchProps = {};
type OwnProps = {
  isHighLevelRole: boolean;
  isCoach?: boolean;
  fromManageUsers?: boolean;
  handleSubmit: (renewedTeacherInfo: RenewedTeacherInfo, activeStatus: boolean) => any;
  onFieldValidated?: (isValid: boolean) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

const TeacherInfoForm: FunctionComponent<Props> = (props) => {
  const {
    isHighLevelRole,
    selectedTeacher,
    schools,
    hasDistrictPermission,
    isCoach,
    fromManageUsers,
    handleSubmit,
    onFieldValidated,
  } = props;

  const { isDistrictOwner } = useUserRole();

  const [renewedTeacherInfo, setRenewedTeacherInfo] = useState<
    RenewedTeacherInfo
  >({
    first_name: "",
    last_name: "",
    email: "",
    schools: [],
    role: undefined,
  });
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getSchools());
  }, []);

  const [tempActiveStatus, setTempActiveStatus] = useState(
  !selectedTeacher?.user.profile.account_disabled
);


  useEffect(() => {
    if (selectedTeacher) {
      const schools =
        selectedTeacher?.user.profile.current_assignment?.schools.map(
          (school) => school.id
        ) ?? [];

      const { first_name, last_name } = splitStudentName(selectedTeacher.user);
      
      if(fromManageUsers) {
        onFieldValidated && onFieldValidated(schools.length > 0 
          && first_name !== '' 
          && last_name !== '');
      }


      setRenewedTeacherInfo({
        first_name,
        last_name,
        email: !selectedTeacher.user?.email?.includes('@njtss_user_imported') ? selectedTeacher.user.email : '',
        schools: schools,
        //role: selectedTeacher.user.profile.current_assignment?.role,
      });
      setTempActiveStatus(!selectedTeacher.user.profile.account_disabled);
    }
  }, [selectedTeacher]);

  useEffect(() => {
    if(fromManageUsers) {
      onFieldValidated && onFieldValidated(schools.filter((school) =>
          renewedTeacherInfo.schools.find((rSchool) => rSchool === school.id)
        ).length > 0
      && renewedTeacherInfo.first_name !== ''
      && renewedTeacherInfo.last_name !== '');
    }
  }, [renewedTeacherInfo]);

  const handleTeacherNameChange = (event: React.FormEvent<any>) => {
    const { name, value } = event.target as HTMLInputElement;
    const sanitizedValue = escapeSpecialCharacter(value);
    setRenewedTeacherInfo((ps) => ({ ...ps, [name]: sanitizedValue }));
     if(fromManageUsers) {
      onFieldValidated && onFieldValidated(sanitizedValue !== '');
    }
  };

  const handleEmailChange = (event: React.FormEvent<any>) => {
    const { name, value } = event.target as HTMLInputElement;
    setRenewedTeacherInfo((ps) => ({ ...ps, [name]: value }));
  };

  const handleSchoolsChange = (newValue?: ValueType<School, true>) => {
    const newSchools = newValue
      ? (newValue as Array<School>).map((val) => val.id)
      : [];
    setRenewedTeacherInfo((ps) => ({ ...ps, schools: newSchools }));
    if(fromManageUsers) {
      onFieldValidated && onFieldValidated(newSchools.length > 0);
    }
  };

  const handleRoleChange = (newRole?: ValueType<any, false>) => {
    setRenewedTeacherInfo((ps) => ({ ...ps, role: newRole }));
  };

  const loadingResetPassword = useSelector<
    ApplicationState,
    boolean | undefined
  >((s) => s.auth.isLoading.resetPassword);
  const errorResetPassword = useSelector<ApplicationState, string | undefined>(
    (s) => s.auth.errors.resetPassword
  );
  const onResetPasswordSuccess = useCallback(() => {
    toastr.success("Success", "An email has been sent.");
  }, []);
  useLoading(loadingResetPassword, errorResetPassword, onResetPasswordSuccess);
  const handlePasswordResetConfirm = () => {
    const onConfirm = () => {
      if (selectedTeacher?.user.email) {
        dispatch(resetPassword(selectedTeacher.user.email));
      }
    };
    dispatch(
      showConfirmDialog({
        onConfirm,
        centered: true,
        title: "Reset Password",
        text: (
          <>
            Are you sure you want to reset password for{" "}
            <strong>{selectedTeacher?.user.email}</strong>?
          </>
        ),
        confirmButtonText: "Confirm",
      })
    );
  };

  const isDisabled = useMemo(() => {
    return (!isHighLevelRole && selectedTeacher?.user.profile.is_active) || isCoach;
  }, [isHighLevelRole, selectedTeacher?.user.profile.is_active, isCoach]);

  const [loadingChangeStatus, setLoadingChangeStatus] = useState(false);
  const handleActiveStatusToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
  setTempActiveStatus(e.target.checked);
};
  return (
    <Form id="teacherInfoForm" onSubmit={(e) => {e.preventDefault();handleSubmit(renewedTeacherInfo, tempActiveStatus)(e);}}>
      {isHighLevelRole && (
        <div className="mb-3 d-flex">
          <h4 className="font-weight-bold mr-2">User Status</h4>
          <span className="list-order-swither-checkbox d-flex">
            <label>Disabled</label>  
            <Form.Check
              type="switch"
              id="active-status-switch"
              label='Active'
              className="ml-2"
              checked={tempActiveStatus}
              onChange={handleActiveStatusToggle}
            />
          </span>
          {loadingChangeStatus && (
            <Spinner animation="border" size="sm" className="ml-2" />
          )}
        </div>
      )}

      <div className="form-row">
      <div className="form-group col-md-6">
        <label className="font-weight-bold">First Name{isDistrictOwner ? <span className="label-asterisk" style={{ fontSize: "0.8em" }}>*</span> : null}</label>
        <Form.Control
          required
          type="text"
          name={"first_name"}
          placeholder="Enter"
          onChange={handleTeacherNameChange}
          disabled={isDisabled}
          value={renewedTeacherInfo.first_name}
        />
      </div>
      <div className="form-group col-md-6">
        <label className="font-weight-bold">Last Name{isDistrictOwner ? <span className="label-asterisk" style={{ fontSize: "0.8em" }}>*</span> : null}</label>
        <Form.Control
          required
          type="text"
          name={"last_name"}
          placeholder="Enter"
          onChange={handleTeacherNameChange}
          disabled={isDisabled}
          value={renewedTeacherInfo.last_name}
        />
      </div>
    </div>

    <div className="form-group">
      <label> Which school(s) does <span className="font-weight-bold">{selectedTeacher ? `${selectedTeacher.user.first_name || ""}
         ${selectedTeacher.user.last_name || ""}`
         .trim() : "educator"}</span> work in?
         {isDistrictOwner ? <span className="label-asterisk" style={{ fontSize: "0.8em" }}>*</span> : null}
      </label>
      <Select
        isMulti
        name="schools"
        value={schools.filter((school) =>
          renewedTeacherInfo.schools.find((rSchool) => rSchool === school.id)
        )}
        getOptionLabel={({ name }: School) => name}
        getOptionValue={(school: School) => school.id.toString()}
        isDisabled={isDisabled}
        options={schools}
        onChange={handleSchoolsChange}
        className="schoolSelect"
        placeholder="Select"
      />
    </div>
    </Form>
  );
};

const mapStateToProps = ({
  onboarding,
  auth,
}: ApplicationState): StateProps => {
  return {
    hasDistrictPermission:
      auth.userInfo?.profile.current_assignment?.role &&
      districtRoles.includes(auth.userInfo.profile.current_assignment.role),
    schools: onboarding.schools,
    selectedTeacher: onboarding.selectedTeacher,
  };
};

export default connect(mapStateToProps)(TeacherInfoForm);
